// Theme-owned launcher uses Smile's documented #smile-home link, never its internal markup.
// It copies the visible Gorgias "Contact us" pill's width, height and right edge, stacked 8px above it.
const GAP = 8;

class RewardsLauncher extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.queue = () => {
      if (this.pending) return;
      this.pending = requestAnimationFrame(() => {
        this.pending = 0;
        this.align();
      });
    };
    window.addEventListener('resize', this.queue, { signal: this.controller.signal });
    this.observer = new MutationObserver(this.queue);
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.resizeObserver = new ResizeObserver(this.queue);
    // Gorgias sizes its pill a moment after load; re-measure for a short while.
    this.settle = setInterval(this.queue, 400);
    setTimeout(() => clearInterval(this.settle), 12000);
    this.queue();
  }

  // The visible pill: the largest rendered button inside the chat frame (not a hidden/close control).
  visiblePill(frame) {
    try {
      const doc = frame.contentDocument;
      if (!doc) return null;
      let best = null;
      let bestArea = 0;
      for (const el of doc.querySelectorAll('button, [role="button"]')) {
        const r = el.getBoundingClientRect();
        const area = r.width * r.height;
        if (r.width > 0 && r.height > 0 && area > bestArea) {
          best = r;
          bestArea = area;
        }
      }
      return best;
    } catch {
      return null; // Cross-origin frame: fall back to the frame's own bounds.
    }
  }

  align() {
    const frame = document.querySelector('#gorgias-chat-container iframe#chat-button, #gorgias-chat-container iframe[title*="button" i]');
    if (!frame) return;
    if (frame !== this.chatFrame) {
      this.resizeObserver.disconnect();
      this.chatFrame = frame;
      this.resizeObserver.observe(frame);
      frame.addEventListener('load', this.queue, { signal: this.controller.signal });
    }
    const outer = frame.getBoundingClientRect();
    if (!outer.width || !outer.height) return;
    const inner = this.visiblePill(frame);
    const pill = inner
      ? { left: outer.left + inner.left, top: outer.top + inner.top, width: inner.width, height: inner.height }
      : { left: outer.left, top: outer.top, width: outer.width, height: outer.height };

    // clientWidth/Height exclude the scrollbar, which is what fixed right/bottom are measured from.
    const viewportW = document.documentElement.clientWidth;
    const viewportH = document.documentElement.clientHeight;
    const set = (el, prop, value) => el.style.setProperty(prop, value, 'important');
    set(this, 'right', `${Math.max(0, Math.round(viewportW - (pill.left + pill.width)))}px`);
    set(this, 'bottom', `${Math.max(0, Math.round(viewportH - pill.top + GAP))}px`);
    set(this, 'width', `${Math.round(pill.width)}px`);

    // Override the theme's global button min-height/padding so the box matches the pill exactly.
    const button = this.querySelector('a, button');
    if (button) {
      set(button, 'box-sizing', 'border-box');
      set(button, 'width', '100%');
      set(button, 'height', `${Math.round(pill.height)}px`);
      set(button, 'min-height', '0');
      set(button, 'padding', '0 12px');
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    clearInterval(this.settle);
    cancelAnimationFrame(this.pending);
  }
}

if (!customElements.get('s-rewards-launcher')) {
  customElements.define('s-rewards-launcher', RewardsLauncher);
}
